import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, ClipboardListIcon, ShoppingCartIcon, RepeatIcon, CalendarDaysIcon, ZapIcon } from 'lucide-react';
import { tomos } from '../data/content';
import { CTAButton } from './CTAButton';
const insideEach = [{
  icon: ClipboardListIcon,
  title: 'Recetas completas paso a paso',
  desc: 'Con ingredientes de supermercado argentino, tiempos reales y técnicas explicadas en lenguaje simple.'
}, {
  icon: ShoppingCartIcon,
  title: 'Listas de compras por perfil',
  desc: 'Para carne, pescado, vegetariano, vegano, económico, familiar y cero cocina.'
}, {
  icon: RepeatIcon,
  title: 'Tablas de reemplazos inteligentes',
  desc: 'Para saber en segundos con qué reemplazás pan, harina, azúcar, salsas y lácteos.'
}, {
  icon: CalendarDaysIcon,
  title: 'Planillas semanales y mensuales imprimibles',
  desc: 'Para organizar el menú, la compra y el Batch Cooking sin pensar.'
}, {
  icon: ZapIcon,
  title: 'Protocolos de emergencia',
  desc: 'Para cuando no hay nada en casa, cuando llegás agotada o cuando todo se desbordó.'
}];
const totals = ['Más de 100 recetas completas', '9 sistemas de organización imprimibles', '6 guías especializadas por tipo de alimentación', 'Más de 500 páginas de contenido accionable', 'Todo pensado para Argentina, para el supermercado argentino y para la vida real argentina'];
export function TomosSection() {
  return <section className="bg-cream py-20 sm:py-28" aria-labelledby="tomos-title" data-id="element-197">
      <div className="mx-auto max-w-5xl px-5" data-id="element-198">
        <div className="text-center" data-id="element-199">
          <span className="inline-flex items-center gap-2 rounded-full bg-coral/15 px-4 py-1.5 text-sm font-semibold uppercase tracking-widest text-coral-deep" data-id="element-200">
            <BookOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-201" />
            Te presentamos
          </span>
          <h2 id="tomos-title" className="mx-auto mt-6 max-w-3xl font-serif text-3xl font-semibold leading-tight text-ink sm:text-5xl" data-id="element-202">
            Pack Educación Saludable
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-relaxed text-ink/75" data-id="element-203">
            El sistema completo de alimentación tiroidea para quienes quieren
            comer bien, comer rico y dejar de improvisar todos los días. No es
            un recetario genérico. No es información médica. Es el sistema
            completo que conecta lo que sabés con lo que podés hacer en tu
            cocina, tu supermercado y tu vida real.
          </p>
          <p className="mt-6 font-serif text-lg font-semibold text-sage-deep" data-id="element-204">
            9 tomos. Cada uno para un momento distinto de tu día y tu vida.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2" data-id="element-205">
          {tomos.map((tomo, i) => <motion.article key={tomo.number} initial={{
          opacity: 0,
          y: 26
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i % 2 * 0.05
        }} className="flex flex-col rounded-3xl bg-white p-6 shadow-sm ring-1 ring-sage/10" data-id="element-206">
              <div className="flex items-center gap-3" data-id="element-207">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sage-deep font-serif text-lg font-bold text-cream" data-id="element-208">
                  {i + 1}
                </span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-coral" data-id="element-209">
                  {tomo.number}
                </span>
              </div>
              <h3 className="mt-4 font-serif text-xl font-semibold leading-snug text-ink" data-id="element-210">
                {tomo.title}
              </h3>
              <p className="mt-2 text-sm font-medium italic text-sage-deep" data-id="element-211">
                {tomo.subtitle}
              </p>
              <p className="mt-3 text-[14px] leading-relaxed text-ink/70" data-id="element-212">
                {tomo.body}
              </p>
              <p className="mt-4 border-t border-sage/15 pt-4 text-[14px] font-semibold leading-relaxed text-ink" data-id="element-213">
                {tomo.closing}
              </p>
            </motion.article>)}
        </div>

        {/* Dentro de cada tomo */}
        <div className="mt-20" data-id="element-214">
          <h3 className="text-center font-serif text-2xl font-semibold text-ink sm:text-3xl" data-id="element-215">
            Dentro de cada tomo
          </h3>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-216">
            {insideEach.map(f => <div key={f.title} className="rounded-2xl bg-white p-6 ring-1 ring-sage/10" data-id="element-217">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage/15" data-id="element-218">
                  <f.icon className="h-5 w-5 text-sage-deep" aria-hidden="true" data-id="element-219" />
                </span>
                <h4 className="mt-4 font-semibold text-ink" data-id="element-220">{f.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-ink/70" data-id="element-221">
                  {f.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* En total */}
        <div className="mt-14 rounded-3xl bg-sage-deep p-8 text-center sm:p-12" data-id="element-222">
          <h3 className="font-serif text-xl font-semibold uppercase tracking-wide text-cream" data-id="element-223">
            En total
          </h3>
          <ul className="mx-auto mt-6 grid max-w-2xl gap-3 text-left sm:grid-cols-2" data-id="element-224">
            {totals.map(t => <li key={t} className="flex items-start gap-2 text-[15px] leading-snug text-cream/90" data-id="element-225">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-coral" aria-hidden="true" data-id="element-226" />
                {t}
              </li>)}
          </ul>
          <div className="mt-8" data-id="element-227">
            <CTAButton variant="coral" size="lg" data-id="element-228">
              Quiero mi Mega Pack Tiroides
            </CTAButton>
          </div>
        </div>
      </div>
    </section>;
}